import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DmcPackageService } from 'src/app/business-service/dmc-package/dmc-package.service';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { OCCUPENCY_TYPES, ROUTE_CRAFT_TYPES } from 'src/app/modules/shared/constants/api-constants';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-dmc-package-quote',
  templateUrl: './dmc-package-quote.component.html',
  styleUrls: ['./dmc-package-quote.component.scss'],
})
export class DmcPackageQuoteComponent implements OnInit {

  hotelPricingForm!: FormGroup;

  @Input() leadId: any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();
  @Output() updateQuote: EventEmitter<void> = new EventEmitter();

  @Input() storedFormData: any;
  @Input() editQuote: boolean = false;
  @Input() itineraryDetails: any;

  @Input() dmcOrganizationId: any;

  @Input() dmcPackageId: any;
  dmcPackageDetails: any;

  OCCUPENCY_TYPES = OCCUPENCY_TYPES;
  ROUTE_CRAFT_TYPES = ROUTE_CRAFT_TYPES;

  constructor(
    private _globalService: GlobalService,
    private _dmcPackageService: DmcPackageService,
    private _fb: FormBuilder,
    private _validationService: InputValidationsService,
    private _itineraryService: ItineraryService
  ) { }

  async ngOnInit() {    
    if(!this.dmcOrganizationId){
      this.dmcOrganizationId = this.itineraryDetails?.recommendedOrganizationId;
    }
    this.hotelPricingForm = this._fb.group({
      itineraryId: [this.itineraryId],
      packageId: [],
      id: [null],
      organizationId: [null],
      ownership: [''],
      currency: ['USD', [Validators.required]],
      packages: this._fb.array([this.createPackageFormgroup()]),
      notes: ['']
    });

    this.hotelPricingForm.valueChanges.subscribe(() => {
      this.hotelPricingForm.markAsTouched();
      this.hotelPricingForm.markAsDirty();
    })

    if(!this.itineraryDetails){
      return;
    }
    const totalPassengers = this.itineraryDetails?.totalAdults + this.itineraryDetails?.totalChildrens;
    this.dmcPackageDetails = await this.getDmcPackageBasedOnPassengers(this.dmcPackageId, totalPassengers);
    
    if(this.storedFormData){
      this.patchStoredHotelPricingData(this.storedFormData);
    }else if(this.dmcPackageDetails){
      this.patchHotelPricingData(this.dmcPackageDetails.hotelPricingCollection);
    }
  }

  ngOnChanges(){
    this.ngOnInit();
  }

  async getDmcPackageBasedOnPassengers(dmcPackageId: string, totalPassengers: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this._dmcPackageService.getPackageWithPassengers(dmcPackageId, totalPassengers, this.dmcOrganizationId).subscribe({
        next: (data) => {
          resolve(data);
        },
        error: (error) => {
          console.error('Error fetching DMC Package with passengers', error);
          reject(error);
        }
      })
    })
  }

  // Reactive forms funtions

  createPackageFormgroup(): FormGroup {
    let formGroup = this._fb.group({
      hotelCategory: ['', [Validators.required]],
      locations: this._fb.array([]),
      occupancyPricings: this._fb.array([this.createPaxFormgroup()])
    })
    return formGroup;
  }

  createLocationFormGroup(): FormGroup {
    let formGroup = this._fb.group({
      locationName: ['', [Validators.required]],
      nights: [null, [Validators.required]],
      hotelName: ['', [Validators.required]]
    });
    return formGroup;
  }

  createPaxFormgroup(): FormGroup {
    let formGroup = this._fb.group({
      paxRange: this._fb.group({
        minRange: [null, [Validators.required]],
        maxRange: [null, [Validators.required]],
      }, { validators: this._validationService.minMaxRangeValidator() }),
      occupancy: this._fb.group({
        sgl: [null, []],
        dbl: [null, []],
        tpl: [null, []]
      }),
      childWithBed: [null, []],
      extraChildWithoutBed: [null, []],
      selectedOccupancy: ['', [Validators.required]]
    });
    return formGroup;
  }

  getPackagesFormArray(): FormArray {
    return this.hotelPricingForm.get('packages') as FormArray;
  }

  getLocationsFormArray(packageIndex: number): FormArray {
    return this.getPackagesFormArray().at(packageIndex).get('locations') as FormArray;
  }

  getOccupancyPricingsFormArray(packageIndex: number): FormArray {
    return this.getPackagesFormArray().at(packageIndex).get('occupancyPricings') as FormArray;
  }

  occupancyChanged(event:any, packageIndex: number, occupancyIndex:number) {
    let selectedValue = event?.target?.value;
    if(selectedValue){
      this.getOccupancyPricingsFormArray(packageIndex).at(occupancyIndex).patchValue({ selectedOccupancy: selectedValue });
    }
  }

  // Patch form data funtions

  patchHotelPricingData(data: any) {
    const { packages, ...remainingData } = data;
    this.hotelPricingForm.patchValue({
      ...remainingData,
      id: null,
      organizationId:null,
      ownership: '',
      itineraryId: this.itineraryId
    });
    this.patchPackageDetails(packages);
    this.hotelPricingForm.markAsPristine();
    this.hotelPricingForm.markAsUntouched();
  }

  patchStoredHotelPricingData(data: any) {
    const { packages, ...remainingData } = data;
    this.hotelPricingForm.patchValue(remainingData);
    this.patchPackageDetails(packages);
    this.hotelPricingForm.markAsPristine();
    this.hotelPricingForm.markAsUntouched();
  }

  patchPackageDetails(packages: any) {
    const packagesFormArray = this.getPackagesFormArray();

    // Clear existing packages and repopulate
    packagesFormArray.clear();

    packages.forEach((packageItem: any, index: number) => {
      const { hotelCategory, occupancyPricings, locations } = packageItem;

      // Create new package form group
      const packageFormGroup = this.createPackageFormgroup();
      packageFormGroup.patchValue({ hotelCategory });

      // Patch locations
      const locationsFormArray = packageFormGroup.get('locations') as FormArray;
      locationsFormArray.clear();
      locations.forEach((element: any) => {
        let group = this.createLocationFormGroup();
        group.patchValue(element);
        locationsFormArray.push(group);
      });

      // Add package to form array
      packagesFormArray.push(packageFormGroup);

      // Patch occupancy data separately
      this.patchOccupancyData(occupancyPricings, index);
    });
  }

  patchOccupancyData(occupancyData: any, packageIndex: number) {
    const occupancyFormArray = this.getOccupancyPricingsFormArray(packageIndex);
    // Clear existing occupancy data
    occupancyFormArray.clear();

    occupancyData.forEach((occupancyItem: any) => {
      if(!this.storedFormData){
        // let occupancy = this.checkOneOccupencyFilled(occupancyItem.occupancy)
        // if(occupancy){
        //   occupancyItem.selectedOccupancy = occupancy;
        // }
      }
      const occupancyFormGroup = this.createPaxFormgroup();
      occupancyFormGroup.patchValue(occupancyItem);
      occupancyFormArray.push(occupancyFormGroup);
    });
  }

  checkOneOccupencyFilled(group:any): any{
    const { sgl, dbl, tpl } = group;
    if(sgl &&!dbl &&!tpl){
      return OCCUPENCY_TYPES.SGL;
    }
    if(!sgl && dbl &&!tpl){
      return OCCUPENCY_TYPES.TPL;
    }
    if(!sgl &&!dbl && tpl){
      return OCCUPENCY_TYPES.TPL;
    }
    return null;
  }

  async onSubmit(): Promise<boolean> {
    if (!this.hotelPricingForm.valid) {
      this.hotelPricingForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
      return false;
    }

    const formData = this.hotelPricingForm.getRawValue();

    if (this.editQuote) {
      this.finalizeFormSubmission(formData);
      this.updateQuote.emit();
      return true;
    }

    return this.addHotelCosting(formData);
  }

  private async addHotelCosting(formData: any): Promise<boolean> {
    try {
      const data = await this._itineraryService.addHotelPricing(formData).toPromise();
      // this._responseNotificationService.showMessage("Cost And Pricing Added!");
      this.finalizeFormSubmission(data);
      return true;
    } catch (error) {
      console.error("Error adding cost and pricing", error);
      return false;
    }
  }

  private finalizeFormSubmission(data: any): void {
    this.hotelPricingForm.markAsPristine();
    data.dataSaved = true;
    this.storeFormData.emit(data);
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext() {
    if (await this.onSubmit()) {
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.hotelPricingForm.dirty || this.hotelPricingForm.invalid) && this.hotelPricingForm.touched;
  }

  // Helper funtions
  getTripStartDate(): Date{
    return new Date(this.itineraryDetails?.startDate);
  }

  getRouteDate(routeIndex: number): Date{
    return new Date(this.getTripStartDate()?.setDate(this.getTripStartDate()?.getDate() + routeIndex));
  }

  getDayFromDate(date: Date): any{
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
}
